import MaintenanceImage from '@assets/images/Maintenance.png'
import MediaImage from '@components/MediaImage'
import { KUDOS } from '@globals/paths/useDocumentTitle'
import ConstrainedWidthLayout from '@layouts/ConstrainedWidthLayout'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import PageKudosTitleImage from '@shared/PageKudosTitleImage'
import { StackStyles } from '@shared/routing/RoutingStyles'
import { T, useT } from '@transifex/react'
import { useEffect } from 'react'

export const IMPORT_ERROR_BOUNDARY_UI_KEY = 'moduleImportError-storage'
const isLoadingModuleFailed = (message: string) =>
  /(Loading chunk [\d]+ failed|Loading CSS chunk [\d]+ failed|Failed to fetch dynamically imported module: [^]*|Importing a module script failed|'text\/html' is not a valid JavaScript MIME type)/.test(
    message,
  )

type ErrorBoundaryUIProps = {
  error: Error | null
  resetError: () => void
}

export const ErrorBoundaryUI: React.FC<ErrorBoundaryUIProps> = ({ error, resetError }) => {
  const t = useT()
  const hasPageReloaded = window.localStorage.getItem(IMPORT_ERROR_BOUNDARY_UI_KEY) === 'true'
  useEffect(() => {
    document.title = t('{kudos} - Error!', { kudos: KUDOS })
  }, [])

  const isLoadingModuleFailedError =
    error && error?.message ? isLoadingModuleFailed(error.message) : false

  useEffect(() => {
    if (isLoadingModuleFailedError && !hasPageReloaded) {
      window.localStorage.setItem(IMPORT_ERROR_BOUNDARY_UI_KEY, 'true')
      window.location.reload()
    }
  }, [hasPageReloaded, isLoadingModuleFailedError])

  useEffect(() => {
    if (hasPageReloaded) {
      window.onbeforeunload = () => {
        // Removes error boundary key from localstorage if user refreshes page manually, closes tab/window,
        // or clicks Reload Page.
        window.localStorage.removeItem(IMPORT_ERROR_BOUNDARY_UI_KEY)
      }
    }
  }, [hasPageReloaded])
  return (
    <ConstrainedWidthLayout>
      <Stack useFlexGap spacing={3} sx={StackStyles}>
        <PageKudosTitleImage />
        <Typography variant="h1" textAlign="center">
          <T _str="An error has occurred" />
        </Typography>
        <MediaImage
          imgSrc={MaintenanceImage}
          maxWidth={320}
          maxHeight={320}
          backgroundColor="transparent"
        />
        <Button
          id="btnGenericErrorRefresh"
          variant="contained"
          onClick={() => {
            if (isLoadingModuleFailedError) {
              window.location.reload()
            } else {
              resetError()
            }
          }}>
          <T _str="Reload page" />
        </Button>
      </Stack>
    </ConstrainedWidthLayout>
  )
}

export default ErrorBoundaryUI
